/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Close } from '@carbon/react/icons';
import {
  Button,
  FileUploaderDropContainer,
  FileUploaderItem,
  Loading
} from 'carbon-components-react';
import React, { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { useAppDispatch, useAppSelector } from '../../../store';
import { addMessage, ToastStatus } from '../../ia/store/messages/actions';
import { ingestDocs } from '../store/actions';
import { selectUploadFilesLoading } from '../store/selectors';
import { UploadPanelMessages as messages } from './messages';

import './UploadPanel.scss';
const TWENTY_FIVE_MB = 25000000;
const FIFTY_MB = 50000000;

interface UploadPanelProps {
  closePanel: () => void;
  addedFiles: (number) => void;
  skillId: string;
  assistantId: string;
}

export const UploadPanel = ({
  addedFiles,
  closePanel,
  skillId,
  assistantId
}: UploadPanelProps) => {
  const dispatch = useAppDispatch();
  const filesLoading = useAppSelector(selectUploadFilesLoading);
  const [files, setFiles] = useState([]);
  const [totalFileSize, setTotalFileSize] = useState(0);
  const { formatMessage: t } = useIntl();

  const addFiles = (evt, { addedFiles }) => {
    let combinedFileSize = totalFileSize;
    const filteredFiles = addedFiles.filter(file => {
      if (file.size === 0) {
        dispatch(
          addMessage({
            title: t(messages.error),
            message: `${file.name} ${t(messages.isEmpty)}`,
            status: ToastStatus.ERROR
          })
        );
      } else if (file.size <= TWENTY_FIVE_MB) {
        //Less than equal to 25MB
        combinedFileSize += file.size;
        return file;
      } else {
        dispatch(
          addMessage({
            title: t(messages.error),
            message: `${file.name} ${t(messages.tooLarge)}`,
            status: ToastStatus.ERROR
          })
        );
      }
    });

    if (combinedFileSize > FIFTY_MB) {
      dispatch(
        addMessage({
          title: t(messages.error),
          message: t(messages.totalTooLarge),
          status: ToastStatus.ERROR
        })
      );
    }
    setTotalFileSize(combinedFileSize);
    setFiles([...files, ...filteredFiles]);
  };

  const clearFile = (evt, { uuid }) => {
    let combinedFileSize = totalFileSize;
    const remainingFiles = files.filter((file, index) => {
      if (index !== Number(uuid)) {
        return file;
      } else {
        combinedFileSize -= file.size;
      }
    });
    setTotalFileSize(combinedFileSize);
    setFiles(remainingFiles);
  };

  const onUpload = async () => {
    //Dispatch to the upload route
    let uploadResponse = null;
    uploadResponse = await dispatch(
      ingestDocs({ skillId, assistantId, files })
    );

    if (!uploadResponse?.payload?.errors) {
      addedFiles(files.length);
    }

    //Clear files
    setFiles([]);
    setTotalFileSize(0);
  };

  return (
    <div className="UploadPanel">
      <div className="UploadPanel__container">
        <div className="UploadPanel__header">
          <FormattedMessage {...messages.uploadDocuments} />
          <Button
            kind="ghost"
            hasIconOnly
            size="sm"
            renderIcon={Close}
            tooltipPosition="left"
            iconDescription={t(messages.close)}
            onClick={closePanel}
          />
        </div>
        <div className="UploadPanel__description">
          <FormattedMessage {...messages.maxSize} />
        </div>
        <FileUploaderDropContainer
          accept={['.pdf', '.PDF', '.docx']}
          multiple={true}
          labelText={t(messages.dragDrop)}
          onAddFiles={addFiles}
          disabled={false}
        />
        <div className="UploadPanel__file-list">
          {files.map((file, index) => (
            <div className="UploadPanel__file-item" key={index}>
              <FileUploaderItem
                uuid={index.toString()}
                name={file.name}
                size="md"
                status="edit"
                iconDescription={t(messages.clearFile)}
                onDelete={clearFile}
                key={index}
              />
            </div>
          ))}
        </div>
      </div>
      <Button
        className="UploadPanel__button"
        size="xl"
        disabled={
          files.length === 0 || files.length > 20 || totalFileSize > FIFTY_MB
        }
        onClick={onUpload}>
        <FormattedMessage {...messages.uploadSelectedFiles} />
      </Button>
      {filesLoading && (
        <div className="UploadPanel__loading-overlay">
          <div className="UploadPanel__loading">
            <Loading
              className="UploadPanel__loading-icon"
              withOverlay={false}
            />
            <FormattedMessage {...messages.uploadInProgress} />
          </div>
        </div>
      )}
    </div>
  );
};
